import { FiatOrder, FIAT_ORDER, fiatOrderAdapter } from '@exodus/models'
import delay from 'delay'

import { getUpdatedOrders } from '../shared/utils.js'

class FiatOrdersMonitor {
  #assetsModule
  #authenticated
  #activeWalletAccountAtom
  #config
  #fiatConfigAtom
  #fiatOrdersAtom
  #fiatOrdersNeedsUpdateAtom
  #fiatRamp
  #fiatUserAtom
  #logger
  #started
  #timer
  #providerMaxAge

  constructor({
    assetsModule,
    activeWalletAccountAtom,
    config,
    fiatConfigAtom,
    fiatOrdersAtom,
    fiatOrdersNeedsUpdateAtom,
    fiatRamp,
    fiatUserAtom,
    logger,
  }) {
    this.#assetsModule = assetsModule
    this.#activeWalletAccountAtom = activeWalletAccountAtom
    this.#config = config
    this.#fiatConfigAtom = fiatConfigAtom
    this.#fiatOrdersAtom = fiatOrdersAtom
    this.#fiatOrdersNeedsUpdateAtom = fiatOrdersNeedsUpdateAtom
    this.#fiatRamp = fiatRamp
    this.#fiatUserAtom = fiatUserAtom
    this.#logger = logger
    this.#providerMaxAge = config.providerMaxAge
  }

  #getConfig = async () => {
    const remoteConfig = await this.#fiatConfigAtom.get()

    return {
      fetchInterval: remoteConfig?.monitor?.interval ?? this.#config.fetchInterval,
      maxAge: remoteConfig?.monitor?.maxAge ?? this.#config.maxAge,
      retryInterval: remoteConfig?.monitor?.retryInterval ?? this.#config.retryInterval,
    }
  }

  #tick = async () => {
    if (!this.#authenticated) {
      const user = await this.#fiatUserAtom.get()

      this.#authenticated = user.authenticated

      if (!this.#authenticated) {
        return
      }
    }

    const now = new Date()
    const [orders, inProgressOrders, serverOrders] = await Promise.all([
      this.#fiatOrdersAtom.get(),
      this.#fiatOrdersNeedsUpdateAtom.get(),
      this.#fiatRamp.getOrders(),
    ])

    const failedToFetchOrders = []
    const existingOrderIds = new Set(orders.toJSON().map((order) => order.orderId))
    const nonExistingOrders = []
    const needsUpdateOrders = [
      ...inProgressOrders.toJSON(),
      ...serverOrders.filter((order) => order.providerId && !existingOrderIds.has(order.id)),
    ]

    if (needsUpdateOrders.length === 0) {
      return
    }

    const [activeWalletAccount, { maxAge }] = await Promise.all([
      this.#activeWalletAccountAtom.get(),
      this.#getConfig(),
    ])

    const assets = this.#assetsModule.getAssets()
    const fetchedOrders = await Promise.all(
      needsUpdateOrders.map(async (order) => {
        try {
          const fetchedOrder = await this.#fiatRamp.getOrder(order.orderId || order.id)
          const parsedOrder = FiatOrder.fromJSON(
            fiatOrderAdapter(
              {
                ...fetchedOrder,
                walletAccount: fetchedOrder.walletAccount || activeWalletAccount,
              },
              assets
            )
          )

          const isExpired =
            parsedOrder.exodusStatus !== 'complete' &&
            now - new Date(parsedOrder.date) >
              (this.#providerMaxAge?.[parsedOrder.provider] || maxAge)

          return isExpired
            ? parsedOrder.update({ status: FIAT_ORDER.STALE_STATUS[parsedOrder.provider] })
            : parsedOrder
        } catch (error) {
          if (error.response?.status === 404 && order.orderId) {
            return FiatOrder.fromJSON(order).update({
              status: FIAT_ORDER.STALE_STATUS[order.provider],
            })
          }

          if (order.orderId) {
            failedToFetchOrders.push([error, order])
          } else {
            nonExistingOrders.push([error, order])
          }
        }
      })
    )

    if (failedToFetchOrders.length > 0) {
      this.#logger.warn('Failed to fetch updates for fiat order', failedToFetchOrders)
    }

    if (nonExistingOrders.length > 0) {
      this.#logger.warn('Order does not exist on fiat server', nonExistingOrders)
    }

    const updatedOrders = getUpdatedOrders(fetchedOrders.filter(Boolean), orders)

    if (updatedOrders) {
      await this.#fiatOrdersAtom.set(updatedOrders)
    }
  }

  start = async () => {
    if (this.#started) {
      return
    }

    this.#started = true

    while (this.#started) {
      try {
        await this.#tick()
        if (!this.#started) break

        const { fetchInterval } = await this.#getConfig()

        this.#timer = delay(fetchInterval)
      } catch (error) {
        this.#logger.error('Failed to update fiat orders', error)

        const { retryInterval } = await this.#getConfig()

        this.#timer = delay(retryInterval)
      }

      await this.#timer
    }
  }

  stop = () => {
    this.#started = false
    this.#timer?.clear()
  }
}

const fiatOrdersMonitorDefinition = {
  id: 'fiatOrdersMonitor',
  type: 'monitor',
  factory: (...arguments_) => new FiatOrdersMonitor(...arguments_),
  dependencies: [
    'assetsModule',
    'activeWalletAccountAtom',
    'config',
    'fiatConfigAtom',
    'fiatOrdersAtom',
    'fiatRamp',
    'fiatOrdersNeedsUpdateAtom',
    'fiatUserAtom',
    'logger',
  ],
  public: true,
}

export default fiatOrdersMonitorDefinition
